import React, { useEffect, useState, useCallback } from "react";
import useAPI from "../../Hooks/useAPI";
import "./RolePermissionAssigner.css";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import '../../Components/Page/Page.css';
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faLink, faCheckSquare, faSquare } from '@fortawesome/free-solid-svg-icons';

type Role = { id: string; name: string };
type Permission = { id: string; key: string; description: string };

export const RolePermissionAssigner: React.FC = () => {
  const navigate = useNavigate();
  const {
    getRoles,
    getPermissions,
    getRolePermissions,
    assignPermissionToRole,
    removePermissionFromRole
  } = useAPI();

  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [assignedPermissionIds, setAssignedPermissionIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeButton, setActiveButton] = useState<'check' | 'uncheck' | null>(null);

  const areAllPermissionsAssigned = useCallback(() => {
    return permissions.length > 0 && permissions.every(perm => assignedPermissionIds.has(perm.id));
  }, [permissions, assignedPermissionIds]);

  const loadInitialData = useCallback(async () => {
    const roleData = await getRoles();
    const permData = await getPermissions();
    if (roleData) setRoles(roleData);
    if (permData) setPermissions(permData);
  }, [getRoles, getPermissions]);

  const loadRolePermissions = useCallback(async (roleId: string) => {
    const assigned = await getRolePermissions(roleId);
    if (Array.isArray(assigned)) {
      const ids = new Set(assigned.map((p: any) => p.id || p)); // handle either full objects or just IDs
      setAssignedPermissionIds(ids);
    }
  }, [getRolePermissions]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (selectedRoleId) {
      loadRolePermissions(selectedRoleId);
    } else {
      setAssignedPermissionIds(new Set());
    }
  }, [selectedRoleId, loadRolePermissions]);

  const togglePermission = async (permissionId: string) => {
    if (!selectedRoleId) return;

    const hasPermission = assignedPermissionIds.has(permissionId);

    if (hasPermission) {
      await removePermissionFromRole(selectedRoleId, permissionId);
      setAssignedPermissionIds(prev => {
        const next = new Set(prev);
        next.delete(permissionId);
        return next;
      });
    } else {
      await assignPermissionToRole(selectedRoleId, permissionId);
      setAssignedPermissionIds(prev => new Set(prev).add(permissionId));
    }
  };

  const checkAllPermissions = async () => {
    if (!selectedRoleId || permissions.length === 0) return;
    
    setIsLoading(true);
    setActiveButton('check');
    try {
      for (const perm of permissions) {
        if (!assignedPermissionIds.has(perm.id)) {
          await assignPermissionToRole(selectedRoleId, perm.id);
        }
      }
      // Update state after all operations complete
      const newPermissions = new Set(permissions.map(p => p.id));
      setAssignedPermissionIds(newPermissions);
    } catch (error) {
      console.error("Error checking all permissions:", error);
    } finally {
      setIsLoading(false);
      setActiveButton(null);
    }
  };

  const uncheckAllPermissions = async () => {
    if (!selectedRoleId || permissions.length === 0) return;
    
    setIsLoading(true);
    setActiveButton('uncheck');
    try {
      for (const perm of permissions) {
        if (assignedPermissionIds.has(perm.id)) {
          await removePermissionFromRole(selectedRoleId, perm.id);
        }
      }
      // Update state after all operations complete
      setAssignedPermissionIds(new Set());
    } catch (error) {
      console.error("Error unchecking all permissions:", error);
    } finally {
      setIsLoading(false);
      setActiveButton(null);
    }
  };

  return (
    <div className='generalPage'>
      <div className='pageContainer'>
        {/* <Header /> */}
        <div className="button-container">
          <button 
            className="back-button" 
            onClick={() => navigate('/user-admin')}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
          </button>
        </div>
        <div className="role-permission-editor">
          <div className="section-container">
            <h2><FontAwesomeIcon icon={faLink} /> Assign Permissions to Role</h2>
            
            <div className="role-selector">
              <select
                value={selectedRoleId || ""}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                className="role-select input-field"
              >
                <option value="">Select a role</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedRoleId && (
              <div className="permissions-section">
                <div className="permissions-header">
                  <h3 className="permissions-title">Available Permissions</h3>
                  <div className="permissions-actions">
                    <button 
                      onClick={checkAllPermissions} 
                      className={`permission-action-btn ${activeButton === 'check' ? 'active' : ''}`}
                      disabled={isLoading || activeButton === 'uncheck'}
                    >
                      <FontAwesomeIcon icon={areAllPermissionsAssigned() ? faCheckSquare : faSquare} /> Check All
                    </button>
                    <button 
                      onClick={uncheckAllPermissions} 
                      className={`permission-action-btn ${activeButton === 'uncheck' ? 'active' : ''}`}
                      disabled={isLoading || activeButton === 'check'}
                    >
                      <FontAwesomeIcon icon={areAllPermissionsAssigned() ? faSquare : faCheckSquare} /> Uncheck All
                    </button>
                  </div>
                </div>
                <ul className="permission-list">
                  {permissions.map((perm) => (
                    <li key={perm.id} className="permission-item">
                      <label className="permission-label">
                        <input
                          type="checkbox"
                          checked={assignedPermissionIds.has(perm.id)}
                          onChange={() => togglePermission(perm.id)}
                          className="permission-checkbox"
                          disabled={isLoading}
                        />
                        <div className="permission-info">
                          <span className="permission-key">{perm.key}</span>
                          <span className="permission-desc">{perm.description}</span>
                        </div>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};
